import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  alpha,
} from "@mui/material";
import React, { useState } from "react";
import { Features } from "../../Enums/companyStatus";
import colors from "../../Constants/colors";
import { useFeatures } from "../../Query/useFeatures";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ExtensionOutlined } from "@mui/icons-material";
import { ProfileIconBox } from "../Profile/ProfileSection";

interface FeaturesProps {
  selectedFeatures: number[];
  setSelectedFeatures: React.Dispatch<React.SetStateAction<number[]>>;
  // "card": header and tiles in the style of the profile cards.
  variant?: "default" | "card";
  // Tiles read as not clickable, e.g. while the user page is not in edit mode.
  disabled?: boolean;
}

const FeaturesComponent: React.FC<FeaturesProps> = ({
  selectedFeatures,
  setSelectedFeatures,
  variant = "default",
  disabled = false,
}) => {
  const isCard = variant === "card";
  const { data: featuresData } = useFeatures();
  const { t } = useTranslation("userProfilePage");
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { colors: workspace } = useWorkspaceColors();

  const handleToggleExpandAll = () => {
    setExpandAll((prev) => !prev);
  };

  const handleSelectFeature = (featureId: number) => {
    const updatedFeatures = selectedFeatures.includes(featureId)
      ? selectedFeatures.filter((id) => id !== featureId)
      : [...selectedFeatures, featureId];
    setSelectedFeatures(updatedFeatures);
  };

  return (
    <Box sx={{ width: "100%", mt: isCard ? 0 : 2, mb: isCard ? 0 : 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          ...(isCard
            ? { mb: 2.5 }
            : {
                borderBottom: `1px solid ${colors.gray200}`,
                pb: 0.5,
                mb: 1.5,
              }),
        }}
      >
        {isCard && (
          <ProfileIconBox>
            <ExtensionOutlined />
          </ProfileIconBox>
        )}
        <Typography
          sx={
            isCard
              ? {
                  flex: 1,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: "22px",
                  color: colors.primary_dark,
                }
              : {
                  color: workspace.primary_dark,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }
          }
        >
          {t("selectFeatures")}
        </Typography>
        <IconButton
          size="small"
          onClick={handleToggleExpandAll}
          sx={{
            color: colors.gray500,
            gap: 0.5,
            "&:hover": { color: workspace.primary_dark },
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
          >
            {expandAll ? t("hideDescriptions") : t("showDescriptions")}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              transform: expandAll ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </IconButton>
      </Box>
      <Grid container spacing={1.5}>
        {Object.entries(Features)
          .filter(([key]) => isNaN(Number(key)))
          .map(([key, value]) => {
            const isSelected = selectedFeatures.includes(value as number);
            return (
              <Grid item key={value} xs={6} sm={6} md={4}>
                <Card
                  elevation={0}
                  sx={
                    isCard
                      ? {
                          height: "100%",
                          border: `1px solid ${isSelected ? workspace.accent : colors.gray200}`,
                          backgroundColor: isSelected
                            ? alpha(workspace.accent, 0.06)
                            : colors.white,
                          borderRadius: "12px",
                          transition:
                            "border-color 0.15s, background-color 0.15s",
                          ...(!disabled && {
                            "&:hover": { borderColor: workspace.accent },
                          }),
                        }
                      : {
                          height: "100%",
                          border: `1px solid ${isSelected ? workspace.accent : colors.gray200}`,
                          borderLeft: `4px solid ${isSelected ? workspace.accent : workspace.primary_dark}`,
                          backgroundColor: isSelected
                            ? alpha(workspace.accent, 0.08)
                            : colors.white,
                          borderRadius: 1.5,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: workspace.accent,
                            borderLeftColor: workspace.accent,
                          },
                        }
                  }
                >
                  <CardActionArea
                    disabled={disabled}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      height: "100%",
                    }}
                    onClick={() => handleSelectFeature(value as number)}
                  >
                    <CardContent
                      sx={{ flexGrow: 1, width: "100%", py: 1.25, px: 1.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          position: "relative",
                        }}
                      >
                        {isSelected && (
                          <CheckCircleIcon
                            sx={{
                              fontSize: 16,
                              color: workspace.accent,
                            }}
                          />
                        )}
                        <Typography
                          sx={{
                            fontSize: isMobile ? "0.75rem" : "0.85rem",
                            fontWeight: isSelected ? 600 : 500,
                            color: workspace.primary_dark,
                            textTransform: "capitalize",
                          }}
                        >
                          {key.replace(/_/g, " ").toLowerCase()}
                        </Typography>
                      </Box>
                      <Collapse in={expandAll} timeout="auto" unmountOnExit>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.7rem",
                            color: colors.gray600,
                            mt: 0.5,
                          }}
                        >
                          {
                            featuresData?.features?.find(
                              (feature) => feature.id === value,
                            )?.description
                          }
                        </Typography>
                      </Collapse>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
};

export default FeaturesComponent;
